/**
 * VideoStorageWidget - Admin widget showing video storage usage
 * Displays total storage used, file count, and cleanup option
 */
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  HardDrive,
  Video,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Loader2,
  FileVideo,
  TrendingUp
} from 'lucide-react';

export default function VideoStorageWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/products/admin/video-storage');
      setStats(res.data.storage);
    } catch (error) {
      console.error('Error fetching video storage stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleCleanup = async () => {
    if (!confirm('Remove orphaned video files that are not linked to any product?')) return;
    
    try {
      setCleaning(true);
      const res = await api.delete('/products/admin/cleanup-orphaned-videos');
      toast.success(res.data.message);
      fetchStats();
    } catch (error) {
      toast.error('Failed to cleanup videos');
    } finally {
      setCleaning(false);
    }
  };

  const formatSize = (mb) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const getUsageColor = (mb) => {
    if (mb > 500) return 'text-red-400';
    if (mb > 200) return 'text-amber-400';
    return 'text-emerald-400';
  };

  const getUsagePercentage = (mb) => {
    // Assuming 1GB limit for visual purposes
    return Math.min((mb / 1024) * 100, 100);
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-2xl p-4 border border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <HardDrive className="h-5 w-5 text-purple-400" />
          <h3 className="font-semibold">Video Storage</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-2xl p-4 border border-gray-700 hover:border-gray-600 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-purple-500/20">
            <HardDrive className="h-5 w-5 text-purple-400" />
          </div>
          <h3 className="font-semibold">Video Storage</h3>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={fetchStats}
          className="h-8 w-8 text-gray-400 hover:text-white"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {stats && (
        <div className="space-y-4">
          {/* Storage Usage Bar */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Storage Used</span>
              <span className={getUsageColor(stats.total_mb)}>
                {formatSize(stats.total_mb)}
              </span>
            </div>
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${getUsagePercentage(stats.total_mb)}%`,
                  background: stats.total_mb > 500 
                    ? 'linear-gradient(90deg, #ef4444, #f87171)' 
                    : stats.total_mb > 200 
                      ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                      : 'linear-gradient(90deg, #10b981, #34d399)'
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">of 1 GB recommended limit</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-900/50 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <FileVideo className="h-4 w-4 text-blue-400" />
                <span className="text-xs text-gray-400">Total Files</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.file_count}</p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                <span className="text-xs text-gray-400">Avg Size</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {stats.file_count > 0 
                  ? `${(stats.total_mb / stats.file_count).toFixed(1)} MB`
                  : '0 MB'}
              </p>
            </div>
          </div>

          {/* Storage Alert */}
          {stats.total_mb > 200 && (
            <div className={`flex items-start gap-2 p-3 rounded-lg ${
              stats.total_mb > 500 ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'
            }`}>
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                {stats.total_mb > 500 
                  ? 'Storage usage is high. Consider migrating to cloud storage or cleaning up old videos.'
                  : 'Storage usage is moderate. Monitor regularly.'}
              </div>
            </div>
          )}

          {/* Cleanup Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleCleanup}
            disabled={cleaning}
            className="w-full border-gray-700 hover:bg-gray-700"
          >
            {cleaning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cleaning...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Cleanup Orphaned Videos
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
